import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import { auth, db } from "../firebase";
import TopBar from "../components/common/TopBar";
import "./SupportHistoryPage.css";
import { formatDateTimeEn } from "../utils/digits";

function formatDate(value) {
  try {
    if (!value) return "";
    if (typeof value?.toDate === "function") {
      return formatDateTimeEn(value.toDate(), {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "";
  } catch {
    return "";
  }
}

function getStatusMeta(status, t) {
  switch (status) {
    case "open":
      return {
        label: t("support.status.open", { defaultValue: "Open" }),
        className: "open",
      };
    case "in_progress":
      return {
        label: t("support.status.in_progress", { defaultValue: "In progress" }),
        className: "in-progress",
      };
    case "waiting_user":
      return {
        label: t("support.status.waiting_user", { defaultValue: "Waiting for you" }),
        className: "waiting-user",
      };
    case "resolved":
      return {
        label: t("support.status.resolved", { defaultValue: "Resolved" }),
        className: "resolved",
      };
    case "closed":
      return {
        label: t("support.status.closed", { defaultValue: "Closed" }),
        className: "closed",
      };
    default:
      return {
        label: status || t("support.status.unknown", { defaultValue: "Unknown" }),
        className: "unknown",
      };
  }
}

export default function SupportHistoryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const user = auth.currentUser;

  useEffect(() => {
    if (!user?.uid) {
      setTickets([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "supportTickets"),
      where("uid", "==", user.uid),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setTickets(rows);
        setLoading(false);
      },
      (error) => {
        console.error("support history error:", error);
        setTickets([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [user?.uid]);

  const title = useMemo(
    () => t("support.history_title", { defaultValue: "Support history" }),
    [t]
  );

  return (
    <div className="support-history-root">
      <TopBar title={title} onBack={() => navigate(-1)} />

      <div className="support-history-page">
        <div className="support-history-hero">
          <div className="support-history-hero-icon">🎫</div>
          <div className="support-history-hero-texts">
            <h1 className="support-history-title">
              {t("support.history_heading", { defaultValue: "Support tickets" })}
            </h1>
            <p className="support-history-subtitle">
              {t("support.history_subtitle", {
                defaultValue: "Track your previous requests and open any ticket to view details.",
              })}
            </p>
          </div>
        </div>

        <div className="support-history-actions">
          <button
            className="support-history-new-btn"
            onClick={() => navigate("/support/new/contact")}
          >
            {t("support.new_ticket", { defaultValue: "New ticket" })}
          </button>
        </div>

        {loading ? (
          <div className="support-history-state-card">
            {t("common.loading", { defaultValue: "Loading..." })}
          </div>
        ) : tickets.length === 0 ? (
          <div className="support-history-empty">
            <div className="support-history-empty-icon">💬</div>
            <div className="support-history-empty-title">
              {t("support.empty_title", { defaultValue: "No tickets yet" })}
            </div>
            <div className="support-history-empty-subtitle">
              {t("support.empty_subtitle", {
                defaultValue: "When you create your first ticket, it will appear here so you can follow it later.",
              })}
            </div>

            <button
              className="support-history-empty-btn"
              onClick={() => navigate("/support/new/contact")}
            >
              {t("support.create_first_ticket", { defaultValue: "Create first ticket" })}
            </button>
          </div>
        ) : (
          <div className="support-history-list">
            {tickets.map((ticket, index) => {
              const statusMeta = getStatusMeta(ticket.status, t);
              const preview =
                ticket.lastMessageText ||
                ticket.message ||
                t("support.no_message", { defaultValue: "No message" });

              return (
                <button
                  key={ticket.id}
                  type="button"
                  className="support-history-card"
                  onClick={() => navigate(`/support/ticket/${ticket.id}`)}
                >
                  <div className="support-history-card-top">
                    <div className="support-history-card-left">
                      <div className="support-history-ticket-no">
                        {t("support.ticket_number", {
                          defaultValue: "Ticket",
                        })}{" "}
                        #{index + 1}
                      </div>

                      <div className="support-history-subject">
                        {ticket.subject ||
                          t("support.no_subject", { defaultValue: "No subject" })}
                      </div>
                    </div>

                    <div className={`support-history-status ${statusMeta.className}`}>
                      {statusMeta.label}
                    </div>
                  </div>

                  <div className="support-history-meta-row">
                    <span className="support-history-category">
                      {ticket.category ||
                        t("support.category_unknown", { defaultValue: "Unknown" })}
                    </span>

                    {!!ticket.unreadForUser && ticket.unreadForUser > 0 && (
                      <span className="support-history-unread">
                        {ticket.unreadForUser}
                      </span>
                    )}
                  </div>

                  <div className="support-history-preview">{preview}</div>

                  <div className="support-history-footer">
                    <span className="support-history-date">
                      {formatDate(ticket.lastReplyAt || ticket.updatedAt || ticket.createdAt)}
                    </span>

                    <span className="support-history-open-link">
                      {t("support.open_ticket", { defaultValue: "Open" })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
